import { portalText } from "../../i18n/portal.js";
/**
 * Archive, restore and close — the console gateway's lifecycle slice, over `apps/api/src/routes/workspace/lifecycle/`
 * and `apps/api/src/routes/account/closure/` (design §27.4, §27.9.2). Owned by plan-014 unit **B4**.
 *
 *   readWorkspaceLifecycle      `listWorkspaceLifecycle`           every workspace with its state and open archive
 *   readArchivePreflight        `getWorkspaceArchivePreflight`     the item-by-item table (Now / On archive)
 *   archiveWorkspaceWhenIdle    `archiveWorkspaceWithPreflight`    drain, then archive when the last conversation ends
 *   cancelWorkspaceArchive      `cancelWorkspaceArchive`           an archive still draining goes back to active
 *   restoreWorkspace            `restoreWorkspace`                 archived → active (policy, funding, limits rechecked)
 *   readClosurePreflight        `getAccountClosurePreflight`       blockers, refundable purchased credit, retention
 *   startAccountClosure         `startAccountClosure`              `started`, or `blocked` with the stored blockers
 *   withdrawAccountClosure      `withdrawAccountClosure`           until access is being revoked
 *   listMyAccountClosures       `listMyAccountClosures`            the closures this person can still undo
 *   undoAccountClosure          `undoAccountClosure`               the former Owner, during the grace
 *
 * ── THE GENERATED CLIENT ─────────────────────────────────────────────────────────────────────────────────────
 * The operations are in B4's fragments; the merged spec and `@wetalk/api-client`'s generated client are regenerated by
 * the api-client lock holder (plan-014 §2.3), never by a unit. So this slice reads them through {@link operationsOf},
 * a structural view of the client with exactly these methods (B5's precedent): it compiles before and after the
 * regeneration, and a client that still lacks one raises naming the regeneration — never an empty answer.
 */
import { assertOk, type ApiResult, type WeTalkApiClient } from "@wetalk/api-client";

import type { ConsoleGateway } from "../gateway.js";
import { readArray, readRecord, readText, readWholeNumber } from "../decode.js";
import type { GatewayGap, SliceContext } from "./slice-context.js";

// ── views ─────────────────────────────────────────────────────────────────────────────────────────────────

export type WorkspaceLifecycleStateView = "active" | "draining" | "archived";
export type ArchivePreflightItemCodeView =
  | "conversation_in_progress"
  | "campaign"
  | "channel_binding"
  | "api_key"
  | "dedicated_balance"
  | "recurring_add_on";

export interface ArchivePreflightItemView {
  readonly item: ArchivePreflightItemCodeView;
  readonly count: number | null;
  readonly amountMinor: number | null;
  readonly currency: string | null;
  readonly now: string;
  readonly onArchive: string;
}

export interface WorkspaceLifecycleOperationView {
  readonly operationId: string;
  readonly kind: "archive" | "restore";
  readonly state: "draining" | "settling" | "completed" | "cancelled" | "blocked";
  readonly workspaceId: string;
  readonly preflight: readonly ArchivePreflightItemView[];
  readonly requestedAt: string;
  readonly completedAt: string | null;
  readonly cancelledAt: string | null;
  /** On a cancel: how many numbers and widgets were connected again. */
  readonly reattachedChannelBindingCount: number | null;
}

export interface WorkspaceLifecycleRowView {
  readonly workspaceId: string;
  readonly name: string;
  readonly state: WorkspaceLifecycleStateView;
  readonly archivedAt: string | null;
  readonly openOperationId: string | null;
}

export interface ArchivePreflightView {
  readonly workspace: { readonly workspaceId: string; readonly name: string; readonly state: WorkspaceLifecycleStateView };
  readonly item: readonly ArchivePreflightItemView[];
  readonly openOperation: WorkspaceLifecycleOperationView | null;
}

export type ClosureBlockerCodeView = "conversation_in_progress" | "unpaid_statement" | "balance_owed" | "payment_in_progress" | "payout_obligation";

export interface ClosureBlockerView {
  readonly code: ClosureBlockerCodeView;
  readonly detail: string;
}

export type AccountClosureStateView =
  | "preflight_blocked"
  | "draining"
  | "refunding"
  | "cancelling_commercial"
  | "revoking_access"
  | "grace"
  | "purging_content"
  | "closed"
  | "withdrawn"
  | "undone";

export interface AccountClosureView {
  readonly operationId: string;
  readonly state: AccountClosureStateView;
  readonly blocker: readonly ClosureBlockerView[];
  readonly refundChoice: "refund" | "waive" | "none";
  readonly refundRequest: readonly { readonly billingAccountId: string; readonly netMinor: number; readonly currency: string }[];
  readonly graceUntil: string | null;
  readonly financialRetainUntil: string | null;
  readonly requestedAt: string;
  readonly closedAt: string | null;
}

export interface ClosurePreflightView {
  readonly blocker: readonly ClosureBlockerView[];
  readonly refundable: readonly { readonly billingAccountId: string; readonly refundableMinor: number; readonly currency: string }[];
  readonly refundableMinor: number;
  readonly currency: string;
  readonly retention: { readonly contentPurgeAfterDay: number; readonly financialKeepYear: number };
  readonly latestClosure: AccountClosureView | null;
}

export interface StartAccountClosureInput {
  readonly refundChoice: "refund" | "waive" | "none";
  /** The owner's own words, with `waive`. */
  readonly refundWaiver: string | null;
}

export type AccountClosureStartView =
  | { readonly kind: "started"; readonly closure: AccountClosureView }
  | { readonly kind: "blocked"; readonly blocker: readonly ClosureBlockerView[] };

export interface MyAccountClosureView {
  readonly accountId: string;
  readonly accountName: string;
  readonly operationId: string;
  readonly graceUntil: string;
}

declare module "../gateway.js" {
  interface ConsoleGateway {
    /** Every workspace of the organization with its lifecycle state (the Archive and close page). */
    readWorkspaceLifecycle(accountId: string): Promise<readonly WorkspaceLifecycleRowView[]>;
    /** What archiving one workspace would do, item by item, and its archive in flight. */
    readArchivePreflight(workspaceId: string): Promise<ArchivePreflightView>;
    /** Confirms the archive: the workspace drains, and archives when its last conversation ends. */
    archiveWorkspaceWhenIdle(workspaceId: string, idempotencyKey: string): Promise<WorkspaceLifecycleOperationView>;
    /** An archive still draining goes back to active (numbers reconnected; keys and campaigns stay off). */
    cancelWorkspaceArchive(workspaceId: string, operationId: string): Promise<WorkspaceLifecycleOperationView>;
    /** Archived → active, after the rechecks. */
    restoreWorkspace(workspaceId: string): Promise<WorkspaceLifecycleOperationView>;
    /** What blocks closing, what is refundable, what is kept and for how long. */
    readClosurePreflight(accountId: string): Promise<ClosurePreflightView>;
    /** Starts closing (Owner, fresh step-up). Blockers come back as `blocked`, never as a thrown error. */
    startAccountClosure(accountId: string, input: StartAccountClosureInput, idempotencyKey: string): Promise<AccountClosureStartView>;
    /** Withdraws a closure before access is being revoked. */
    withdrawAccountClosure(accountId: string, operationId: string): Promise<AccountClosureView>;
    /** The closures this person asked for that are still in their grace. */
    listMyAccountClosures(): Promise<readonly MyAccountClosureView[]>;
    /** Undoes a closure during its grace (the former Owner). */
    undoAccountClosure(accountId: string, operationId: string): Promise<AccountClosureView>;
  }
}

export type LifecycleGatewaySlice = Pick<
  ConsoleGateway,
  | "readWorkspaceLifecycle"
  | "readArchivePreflight"
  | "archiveWorkspaceWhenIdle"
  | "cancelWorkspaceArchive"
  | "restoreWorkspace"
  | "readClosurePreflight"
  | "startAccountClosure"
  | "withdrawAccountClosure"
  | "listMyAccountClosures"
  | "undoAccountClosure"
>;

/** This area's rows of `GATEWAY_GAP`: none — every operation is in B4's fragments. */
export const lifecycleGap: readonly GatewayGap[] = [];

// ── the client, structurally ──────────────────────────────────────────────────────────────────────────────

interface LifecycleOperations {
  listWorkspaceLifecycle(request: { path: { readonly account_id: string } }): Promise<ApiResult>;
  getWorkspaceArchivePreflight(request: { path: { readonly workspace_id: string } }): Promise<ApiResult>;
  archiveWorkspaceWithPreflight(request: { path: { readonly workspace_id: string }; header?: Readonly<Record<string, string>>; body?: unknown }): Promise<ApiResult>;
  cancelWorkspaceArchive(request: { path: { readonly workspace_id: string; readonly workspace_lifecycle_operation_id: string } }): Promise<ApiResult>;
  restoreWorkspace(request: { path: { readonly workspace_id: string } }): Promise<ApiResult>;
  getAccountClosurePreflight(request: { path: { readonly account_id: string } }): Promise<ApiResult>;
  startAccountClosure(request: { path: { readonly account_id: string }; header?: Readonly<Record<string, string>>; body?: unknown }): Promise<ApiResult>;
  withdrawAccountClosure(request: { path: { readonly account_id: string; readonly account_closure_operation_id: string } }): Promise<ApiResult>;
  listMyAccountClosures(request?: Readonly<Record<string, never>>): Promise<ApiResult>;
  undoAccountClosure(request: { path: { readonly account_id: string; readonly account_closure_operation_id: string } }): Promise<ApiResult>;
}

/** The client's methods for this area, by name. Raises for one the generated client does not carry yet. */
function operationsOf(client: WeTalkApiClient): LifecycleOperations {
  const held = client as unknown as Partial<Record<keyof LifecycleOperations, unknown>>;
  return new Proxy({} as LifecycleOperations, {
    get(_target, name: string) {
      const found = held[name as keyof LifecycleOperations];
      if (typeof found !== "function") {
        throw new Error(
          portalText("shell", "the_generated_api_client_has_no_value_regenerate_it_pnpm_a_deb92968", { v0: String(name) }),
        );
      }
      return (found as (...argument: unknown[]) => unknown).bind(client);
    },
  });
}

// ── decoding ──────────────────────────────────────────────────────────────────────────────────────────────

const WORKSPACE_STATE: readonly WorkspaceLifecycleStateView[] = ["active", "draining", "archived"];
const ITEM: readonly ArchivePreflightItemCodeView[] = ["conversation_in_progress", "campaign", "channel_binding", "api_key", "dedicated_balance", "recurring_add_on"];
const OPERATION_STATE: readonly WorkspaceLifecycleOperationView["state"][] = ["draining", "settling", "completed", "cancelled", "blocked"];
const OPERATION_KIND: readonly WorkspaceLifecycleOperationView["kind"][] = ["archive", "restore"];
const BLOCKER: readonly ClosureBlockerCodeView[] = ["conversation_in_progress", "unpaid_statement", "balance_owed", "payment_in_progress", "payout_obligation"];
const CLOSURE_STATE: readonly AccountClosureStateView[] = [
  "preflight_blocked",
  "draining",
  "refunding",
  "cancelling_commercial",
  "revoking_access",
  "grace",
  "purging_content",
  "closed",
  "withdrawn",
  "undone",
];
const REFUND_CHOICE: readonly AccountClosureView["refundChoice"][] = ["refund", "waive", "none"];

function dataOf(result: ApiResult, operationId: string): Readonly<Record<string, unknown>> {
  return readRecord(readRecord(assertOk(result), operationId)["data"], `${operationId}.data`);
}

function textOrNull(value: unknown, path: string): string | null {
  return value === null || value === undefined ? null : readText(value, path);
}

function wholeOrNull(value: unknown, path: string): number | null {
  return value === null || value === undefined ? null : readWholeNumber(value, path);
}

/** A signed integer amount (a payable book can be negative). */
function integerOrNull(value: unknown, path: string): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value !== "number" || !Number.isSafeInteger(value)) throw new Error(`${path}: not a whole number`);
  return value;
}

function oneOf<T extends string>(allowed: readonly T[], value: unknown, path: string): T {
  const text = readText(value, path);
  const found = allowed.find((one) => one === text);
  if (found === undefined) throw new Error(`${path}: "${text}" is not one of ${allowed.join(", ")}`);
  return found;
}

export function preflightItemOf(value: unknown, path: string): ArchivePreflightItemView {
  const row = readRecord(value, path);
  return {
    item: oneOf(ITEM, row["item"], `${path}.item`),
    count: wholeOrNull(row["count"], `${path}.count`),
    amountMinor: integerOrNull(row["amount_minor"], `${path}.amount_minor`),
    currency: textOrNull(row["currency"], `${path}.currency`),
    now: readText(row["now"], `${path}.now`),
    onArchive: readText(row["on_archive"], `${path}.on_archive`),
  };
}

export function lifecycleOperationOf(value: unknown, path: string): WorkspaceLifecycleOperationView {
  const row = readRecord(value, path);
  return {
    operationId: readText(row["operation_id"], `${path}.operation_id`),
    kind: oneOf(OPERATION_KIND, row["kind"], `${path}.kind`),
    state: oneOf(OPERATION_STATE, row["state"], `${path}.state`),
    workspaceId: readText(row["workspace_id"], `${path}.workspace_id`),
    preflight: readArray(row["preflight"], `${path}.preflight`).map((one, index) => preflightItemOf(one, `${path}.preflight[${index}]`)),
    requestedAt: readText(row["requested_at"], `${path}.requested_at`),
    completedAt: textOrNull(row["completed_at"], `${path}.completed_at`),
    cancelledAt: textOrNull(row["cancelled_at"], `${path}.cancelled_at`),
    reattachedChannelBindingCount: wholeOrNull(row["reattached_channel_binding_count"], `${path}.reattached_channel_binding_count`),
  };
}

function blockerOf(value: unknown, path: string): ClosureBlockerView {
  const row = readRecord(value, path);
  return { code: oneOf(BLOCKER, row["code"], `${path}.code`), detail: readText(row["detail"], `${path}.detail`) };
}

export function accountClosureOf(value: unknown, path: string): AccountClosureView {
  const row = readRecord(value, path);
  return {
    operationId: readText(row["operation_id"], `${path}.operation_id`),
    state: oneOf(CLOSURE_STATE, row["state"], `${path}.state`),
    blocker: readArray(row["blocker"], `${path}.blocker`).map((one, index) => blockerOf(one, `${path}.blocker[${index}]`)),
    refundChoice: oneOf(REFUND_CHOICE, row["refund_choice"], `${path}.refund_choice`),
    refundRequest: readArray(row["refund_request"], `${path}.refund_request`).map((one, index) => {
      const at = `${path}.refund_request[${index}]`;
      const request = readRecord(one, at);
      return {
        billingAccountId: readText(request["billing_account_id"], `${at}.billing_account_id`),
        netMinor: readWholeNumber(request["net_minor"], `${at}.net_minor`),
        currency: readText(request["currency"], `${at}.currency`),
      };
    }),
    graceUntil: textOrNull(row["grace_until"], `${path}.grace_until`),
    financialRetainUntil: textOrNull(row["financial_retain_until"], `${path}.financial_retain_until`),
    requestedAt: readText(row["requested_at"], `${path}.requested_at`),
    closedAt: textOrNull(row["closed_at"], `${path}.closed_at`),
  };
}

export function closurePreflightOf(data: Readonly<Record<string, unknown>>): ClosurePreflightView {
  const retention = readRecord(data["retention"], "retention");
  const latest = data["latest_closure"];
  return {
    blocker: readArray(data["blocker"], "blocker").map((one, index) => blockerOf(one, `blocker[${index}]`)),
    refundable: readArray(data["refundable"], "refundable").map((one, index) => {
      const at = `refundable[${index}]`;
      const row = readRecord(one, at);
      return {
        billingAccountId: readText(row["billing_account_id"], `${at}.billing_account_id`),
        refundableMinor: readWholeNumber(row["refundable_minor"], `${at}.refundable_minor`),
        currency: readText(row["currency"], `${at}.currency`),
      };
    }),
    refundableMinor: readWholeNumber(data["refundable_minor"], "refundable_minor"),
    currency: readText(data["currency"], "currency"),
    retention: {
      contentPurgeAfterDay: readWholeNumber(retention["content_purge_after_day"], "retention.content_purge_after_day"),
      financialKeepYear: readWholeNumber(retention["financial_keep_year"], "retention.financial_keep_year"),
    },
    latestClosure: latest === null || latest === undefined ? null : accountClosureOf(latest, "latest_closure"),
  };
}

/** The `409 closure_blocked` envelope's `detail.blocker[]`, or `null` when the answer is anything else. */
export function blockedOf(result: ApiResult): readonly ClosureBlockerView[] | null {
  if (result.status !== 409) return null;
  const body = result.body as { readonly error?: { readonly code?: unknown; readonly detail?: { readonly blocker?: unknown } } } | null;
  if (body?.error?.code !== "closure_blocked") return null;
  return readArray(body.error.detail?.blocker, "error.detail.blocker").map((one, index) => blockerOf(one, `error.detail.blocker[${index}]`));
}

// ── the slice ─────────────────────────────────────────────────────────────────────────────────────────────

export function lifecycleApiSlice(client: WeTalkApiClient, _context: SliceContext): LifecycleGatewaySlice {
  const operation = operationsOf(client);
  return {
    async readWorkspaceLifecycle(accountId) {
      const result = await operation.listWorkspaceLifecycle({ path: { account_id: accountId } });
      const body = readRecord(assertOk(result), "listWorkspaceLifecycle");
      return readArray(body["data"], "listWorkspaceLifecycle.data").map((one, index) => {
        const at = `listWorkspaceLifecycle.data[${String(index)}]`;
        const row = readRecord(one, at);
        return {
          workspaceId: readText(row["workspace_id"], `${at}.workspace_id`),
          name: readText(row["name"], `${at}.name`),
          state: oneOf(WORKSPACE_STATE, row["state"], `${at}.state`),
          archivedAt: textOrNull(row["archived_at"], `${at}.archived_at`),
          openOperationId: textOrNull(row["open_operation_id"], `${at}.open_operation_id`),
        };
      });
    },
    async readArchivePreflight(workspaceId) {
      const data = dataOf(await operation.getWorkspaceArchivePreflight({ path: { workspace_id: workspaceId } }), "getWorkspaceArchivePreflight");
      const workspace = readRecord(data["workspace"], "workspace");
      const open = data["open_operation"];
      return {
        workspace: {
          workspaceId: readText(workspace["workspace_id"], "workspace.workspace_id"),
          name: readText(workspace["name"], "workspace.name"),
          state: oneOf(WORKSPACE_STATE, workspace["state"], "workspace.state"),
        },
        item: readArray(data["item"], "item").map((one, index) => preflightItemOf(one, `item[${index}]`)),
        openOperation: open === null || open === undefined ? null : lifecycleOperationOf(open, "open_operation"),
      };
    },
    async archiveWorkspaceWhenIdle(workspaceId, idempotencyKey) {
      const result = await operation.archiveWorkspaceWithPreflight({
        path: { workspace_id: workspaceId },
        header: { "idempotency-key": idempotencyKey },
        body: { in_progress_action: "finish" },
      });
      return lifecycleOperationOf(dataOf(result, "archiveWorkspaceWithPreflight"), "archiveWorkspaceWithPreflight.data");
    },
    async cancelWorkspaceArchive(workspaceId, operationId) {
      const result = await operation.cancelWorkspaceArchive({ path: { workspace_id: workspaceId, workspace_lifecycle_operation_id: operationId } });
      return lifecycleOperationOf(dataOf(result, "cancelWorkspaceArchive"), "cancelWorkspaceArchive.data");
    },
    async restoreWorkspace(workspaceId) {
      return lifecycleOperationOf(dataOf(await operation.restoreWorkspace({ path: { workspace_id: workspaceId } }), "restoreWorkspace"), "restoreWorkspace.data");
    },
    async readClosurePreflight(accountId) {
      return closurePreflightOf(dataOf(await operation.getAccountClosurePreflight({ path: { account_id: accountId } }), "getAccountClosurePreflight"));
    },
    async startAccountClosure(accountId, input, idempotencyKey) {
      const result = await operation.startAccountClosure({
        path: { account_id: accountId },
        header: { "idempotency-key": idempotencyKey },
        body: input.refundChoice === "waive" ? { refund_choice: "waive", refund_waiver: input.refundWaiver } : { refund_choice: input.refundChoice },
      });
      const blocked = blockedOf(result);
      if (blocked !== null) return { kind: "blocked", blocker: blocked };
      return { kind: "started", closure: accountClosureOf(dataOf(result, "startAccountClosure"), "startAccountClosure.data") };
    },
    async withdrawAccountClosure(accountId, operationId) {
      const result = await operation.withdrawAccountClosure({ path: { account_id: accountId, account_closure_operation_id: operationId } });
      return accountClosureOf(dataOf(result, "withdrawAccountClosure"), "withdrawAccountClosure.data");
    },
    async listMyAccountClosures() {
      const body = readRecord(assertOk(await operation.listMyAccountClosures({})), "listMyAccountClosures");
      return readArray(body["data"], "listMyAccountClosures.data").map((one, index) => {
        const at = `listMyAccountClosures.data[${String(index)}]`;
        const row = readRecord(one, at);
        return {
          accountId: readText(row["account_id"], `${at}.account_id`),
          accountName: readText(row["account_name"], `${at}.account_name`),
          operationId: readText(row["operation_id"], `${at}.operation_id`),
          graceUntil: readText(row["grace_until"], `${at}.grace_until`),
        };
      });
    },
    async undoAccountClosure(accountId, operationId) {
      const result = await operation.undoAccountClosure({ path: { account_id: accountId, account_closure_operation_id: operationId } });
      return accountClosureOf(dataOf(result, "undoAccountClosure"), "undoAccountClosure.data");
    },
  };
}
